import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import './ToastManager.css';

const ToastContext = createContext(null);

export function useToast() {
  return useContext(ToastContext);
}

const ICONS = {
  success: '✓',
  error:   '✕',
  warning: '⚠',
  info:    'ℹ',
};

const AUTO_DURATION = {
  success: 4000,
  error:   6000,
  warning: 5000,
  info:    4000,
};

function ToastItem({ toast, onDismiss }) {
  const [exiting, setExiting] = useState(false);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 250);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    const t = setTimeout(dismiss, toast.duration);
    return () => clearTimeout(t);
  }, [toast.duration, dismiss]);

  return (
    <div
      className={`tm-toast tm-toast--${toast.variant}${exiting ? ' tm-toast--out' : ''}`}
      role="alert"
    >
      <span className="tm-icon">{ICONS[toast.variant]}</span>
      <span className="tm-msg">{toast.message}</span>
      <button className="tm-close" onClick={dismiss} aria-label="Cerrar">✕</button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback(({ message, variant = 'info', duration }) => {
    const id = Date.now() + Math.random();
    const d  = duration ?? AUTO_DURATION[variant] ?? 4000;
    // Keep max 4 toasts; discard oldest when exceeded
    setToasts(prev => [...prev.slice(-3), { id, message, variant, duration: d }]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="tm-container" aria-live="polite" aria-atomic="false">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
